import React, { useEffect, useState } from "react";
import {
  ChatAppResponse,
  getFromHistory,
  removeFromHistory,
  SelectedFile,
} from "../api/index.js";
import { getToken, useLogin } from "../authConfig.js";
import { useMsal } from "@azure/msal-react";
import {
  Delete24Regular,
  Add24Regular,
  ChevronDoubleLeft16Regular,
} from "@fluentui/react-icons";
import { Button } from "@fluentui/react-components";
import { DisplayError } from "./DisplayError";
import {
  differenceInCalendarDays,
  parseISO,
  isToday,
  isYesterday,
} from "date-fns";
import { useTranslation } from "react-i18next";
export interface HistoryElement {
  sessionId: string;
  label: string;
  chatContext: [user: string, response: ChatAppResponse][];
  selectedFilters?: Record<string, string[]>;  // Filters used in this chat
  selectedSourcefiles?: SelectedFile[];  // Selected files with ID and name
  updated_at: string;
}
interface ChatHistoryProps {
  setContext: (
    context: [string, ChatAppResponse][], 
    sessionId: string,
    selectedFilters?: Record<string, string[]>,
    selectedSourcefiles?: SelectedFile[]
  ) => void;
  currentSessionId: string;
  clearChat: () => void;
  isHistoryVisible: boolean;
  onClose: () => void;
  onHistorySaved?: number;  // Trigger value that changes after save completes
}

type GroupedHistory = {
  [section: string]: HistoryElement[];
};

function groupHistoryByDate(history: HistoryElement[], t: (key: string) => string): GroupedHistory {
  const groups: GroupedHistory = {
    [t("labels.history.today")]: [],
    [t("labels.history.yesterday")]: [],
    [t("labels.history.last7Days")]: [],
    [t("labels.history.older")]: [],
  };

  history.forEach((item) => {
    const date = parseISO(item.updated_at);

    if (isToday(date)) {
      groups[t("labels.history.today")].push(item);
    } else if (isYesterday(date)) {
      groups[t("labels.history.yesterday")].push(item);
    } else if (differenceInCalendarDays(new Date(), date) <= 7) {
      groups[t("labels.history.last7Days")].push(item);
    } else {
      groups[t("labels.history.older")].push(item);
    }
  });

  return groups;
}


export const ChatHistory: React.FC<ChatHistoryProps> = function ({
  setContext,
  currentSessionId,
  clearChat,
  onClose,
  isHistoryVisible,
  onHistorySaved,
}) {
  const { t } = useTranslation();

  const [history, setHistory] = useState<HistoryElement[]>([]);
  const [error, setError] = useState<string>("");
  const [prevSessionId, setPrevSessionId] = useState<string>(currentSessionId);
  const client = useLogin ? useMsal().instance : undefined;

  const fetchData = async () => {
    try {
      const token = client ? await getToken(client) : undefined;

      let history: HistoryElement[] = await getFromHistory(token);
      history = history.filter((item) => item.chatContext.length !== 0);
      history = history.sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );

      setHistory(history);
    } catch (e) {
      setError(t("labels.history.fetchError"));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Refresh history when onHistorySaved changes (triggered after successful save)
  useEffect(() => {
    if (onHistorySaved !== undefined) {
      fetchData();
    }
  }, [onHistorySaved]);

  const groupedHistory = groupHistoryByDate(history, t);

  return (
    <div className="h-full flex flex-col overflow-y-auto">
      <div className={"h-16 bg-stone-50 flex items-center"}>
        <h1 className={"font-bold ml-4 text-xl"}>{t("labels.chatHistory")}</h1>
        <div className={"grow"}></div>
        <div className={"mr-2"}>
          <Button
            appearance="subtle"
            icon={<ChevronDoubleLeft16Regular />}
            onClick={onClose}
            className={"h-10 w-10"}
            title={t("labels.closeChatHistory")}
          />
        </div>
      </div>
      <div className="px-4 pt-4 pb-6">

        <button
          onClick={clearChat}
          className="w-full h-14 px-6 py-3 flex items-center gap-2 rounded-xl shadow-lg bg-gray-300 hover:bg-gray-300 border border-gray-400 text-gray-800 font-semibold transition-all"
        >
          <Add24Regular />
          {t("labels.newChat")}
        </button>
      </div>



      {Object.entries(groupedHistory).map(([section, items]) =>
        items.length > 0 ? (
          <div key={section} className="mb-4">
            <h2 className="text-sm font-semibold text-gray-500 mb-2 px-2">{section}</h2>
            {items.map((element) => {
              const chatContext = element.chatContext;
              const label = element.label ?? t("labels.newChat");
              const selected = currentSessionId === element.sessionId;

              // Reconstruct full format from minimal format for display compatibility
              const reconstructedContext: [string, ChatAppResponse][] = chatContext.map(([question, response]: any) => {
                // If already in old full format, keep as is
                if (response.message) {
                  return [question, response] as [string, ChatAppResponse];
                }
                // Convert new minimal format to display format
                return [
                  question,
                  {
                    message: { content: response.content || "", role: response.role || "assistant" },
                    session_state: response.session_state,
                    context: { data_points: null, followup_questions: null, thoughts: [] },
                    delta: { role: "assistant", content: "" }
                  }
                ] as [string, ChatAppResponse];
              });

              return (
                <div
                  key={element.sessionId}
                  className={`p-2 rounded border bg-white cursor-pointer h-12 group truncate ${
                    selected && "border-blue-500"
                  } flex items-center hover:bg-stone-50 overflow-hidden`}
                  onClick={() => {
                    setContext?.(
                      reconstructedContext, 
                      element.sessionId,
                      element.selectedFilters,
                      element.selectedSourcefiles
                    );
                  }}
                >
                  <p className="truncate grow"> {label}</p>
                  <div
                    className="opacity-0 group-hover:opacity-100"
                    onClick={async (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      try {
                        const token = client ? await getToken(client) : undefined;
                        await removeFromHistory(token, "123", element.sessionId);
                      } catch (e) {
                        setError(t("labels.history.deleteError"));
                      }
                      fetchData();
                    }}
                  >
                    <Delete24Regular />
                  </div>
                </div>
              );
            })}
          </div>
        ) : null
      )}

      <DisplayError error={error} />
    </div>
  );
};